import { defineStore } from "pinia";

import type { State } from "./Models/State";
import type { DischargeReason } from "@/Modules/Patients/Models/Domain";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";
import { StateNamespace } from "@/Modules/App/Models/State/StateNamespace";

import fetchPatientsAH from "./ActionHandlers/FetchPatients";
import fetchPatientAH from "./ActionHandlers/FetchPatient";
import fetchBreaksAH from "./ActionHandlers/FetchBreaks";
import fetchDischargeReasonsAH from "./ActionHandlers/FetchReasons";
import requestTranscriptionAH from "./ActionHandlers/RequestTranscription";
import sendMissingEmailAH from "../Patients/ActionHandlers/SendMissingEmail";
import dischargePatientAH from "../Patients/ActionHandlers/DischargePatient";

import useAppStore from "@/Modules/App/Store";

import { hasValue } from "@/Utils/Objects";
import { copyToClipboard } from "@/Utils/CopyToClipboard";

const patientsState: State = {
  patients: {},
  selected: null,
  isLoading: false,
  notification: null,
  privatePlans: null,
  sessionsPaymentsRecords: null,
};

export const usePatientsStore = defineStore(StateNamespace.PATIENTS, {
  state: () => ({ ...patientsState }),

  actions: {
    fetchPatients() {
      return fetchPatientsAH();
    },

    fetchPatient(patientUuid: string) {
      return fetchPatientAH(patientUuid);
    },

    fetchBreaks(patientUuid: string) {
      return fetchBreaksAH(patientUuid);
    },

    fetchDischargeReasons(): Promise<DischargeReason[]> {
      return fetchDischargeReasonsAH();
    },

    selectPatient(patientUuid: string) {
      if (hasValue(this.patients[patientUuid])) {
        this.selected = this.patients[patientUuid];
      }
    },

    requestTranscription(status: boolean): Promise<boolean | undefined> {
      return requestTranscriptionAH(status);
    },

    sendMissingEmail(uuid: string): Promise<boolean | undefined> {
      return sendMissingEmailAH(uuid);
    },

    dischargePatient(uuid: string, reason: string): Promise<boolean | undefined> {
      return dischargePatientAH(uuid, reason);
    },

    clearNotification() {
      this.notification = null;
    },

    clear() {
      this.isLoading = false;
      this.notification = null;
    },

    copyPatientId(uuid: string) {
      const appStore = useAppStore();

      copyToClipboard(uuid);

      appStore.showSnackbar({
        message: "patients.field.id.notification_copy_message",
        type: NotificationType.INFO,
      });
    },
  },

  getters: {},
});

export type PatientsStore = ReturnType<typeof usePatientsStore>;

export default usePatientsStore;
